<template>
  <div class="zakat-page container-fluid py-4">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
      <div>
        <div class="d-flex align-items-center gap-2 mb-2">
          <h1 class="h3 mb-0 fw-bold">{{ $t('zakat_calculator') }}</h1>
        </div>
        <p class="text-muted mb-0">{{ $t('zakat_description') }}</p>
      </div>
      <div class="d-flex gap-2 align-items-center">
        <button class="btn btn-outline-secondary btn-sm" @click="refetchPlatformData" :disabled="isFetching">
          <span v-if="isFetching" class="spinner-border spinner-border-sm me-1"></span>
          <AppIcon name="bi-cloud-download" v-else class="bi bi-cloud-download me-1" />
          {{ $t('zakat_refetch') }}
        </button>
        <button class="btn btn-outline-primary btn-sm" @click="resetForm">
          <AppIcon name="bi-arrow-clockwise" class="bi bi-arrow-clockwise me-1" />
          {{ $t('reset') }}
        </button>
      </div>
    </div>

    <!-- Fetch Error Banner -->
    <div v-if="fetchErrors.length > 0" class="alert alert-warning alert-dismissible d-flex align-items-start gap-2 mb-4" role="alert">
      <AppIcon name="bi-exclamation-triangle-fill" class="bi bi-exclamation-triangle-fill text-warning mt-1 flex-shrink-0" />
      <div class="flex-grow-1">
        <strong>{{ $t('zakat_fetch_error_title') }}</strong>
        <ul class="mb-0 mt-1 ps-3">
          <li v-for="err in fetchErrors" :key="err">{{ err }}</li>
        </ul>
      </div>
      <button type="button" class="btn-close" @click="fetchErrors = []"></button>
    </div>

    <div class="row g-4">
      <!-- Left Column: Assets -->
      <div class="col-lg-7">
        <!-- Hijri Year Selection -->
        <div class="card border-0 shadow-sm mb-4 zakat-card">
          <div class="card-body">
            <div class="d-flex align-items-center gap-2 mb-3">
              <div class="icon-circle bg-primary-subtle">
                <AppIcon name="bi-calendar-event" class="bi bi-calendar-event text-primary" />
              </div>
              <div>
                <h6 class="mb-0 fw-bold">{{ $t('zakat_fiscal_year') }}</h6>
                <small class="text-muted">{{ $t('zakat_fiscal_year_desc') }}</small>
              </div>
            </div>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label small fw-semibold text-muted">{{ $t('zakat_year_label') }}</label>
                <input type="text" class="form-control" v-model="zakatYear" :placeholder="currentHijriYear">
              </div>
              <div class="col-md-6">
                <label class="form-label small fw-semibold text-muted">{{ $t('zakat_date') }}</label>
                <input type="date" class="form-control" v-model="zakatDate">
              </div>
            </div>
          </div>
        </div>

        <!-- Assets Section -->
        <div class="card border-0 shadow-sm mb-4 zakat-card">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div class="d-flex align-items-center gap-2">
                <div class="icon-circle bg-success-subtle">
                  <AppIcon name="bi-plus-circle" class="bi bi-plus-circle text-success" />
                </div>
                <div>
                  <h6 class="mb-0 fw-bold">{{ $t('zakat_assets') }}</h6>
                  <small class="text-muted">{{ $t('zakat_assets_desc') }}</small>
                </div>
              </div>
              <span class="badge bg-success-subtle text-success fs-6 px-3 py-2">
                {{ formatMoney(totalAssets) }}
              </span>
            </div>

            <!-- Cash & Bank -->
            <div class="asset-row">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label mb-0 fw-semibold">
                  <AppIcon name="bi-cash-stack" class="bi bi-cash-stack text-success me-2" />
                  {{ $t('zakat_cash_bank') }}
                </label>
                <span class="badge bg-secondary bg-opacity-10 text-muted small">{{ $t('zakat_manual_entry') }}</span>
              </div>
              <div class="input-group">
                <input type="number" class="form-control" v-model.number="assets.cashBank" min="0" step="0.01" placeholder="0.00">
                <span class="input-group-text">{{ currency }}</span>
              </div>
              <small class="text-muted mt-1 d-block">{{ $t('zakat_cash_bank_hint') }}</small>
            </div>

            <!-- Inventory Value -->
            <div class="asset-row">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label mb-0 fw-semibold">
                  <AppIcon name="bi-box-seam" class="bi bi-box-seam text-primary me-2" />
                  {{ $t('zakat_inventory_value') }}
                </label>
                <div class="d-flex align-items-center gap-1">
                  <span v-if="loadingStates.inventory" class="spinner-border spinner-border-sm text-primary" style="width:14px;height:14px;"></span>
                  <span v-else-if="fetchedFields.inventory && !fetchErrors.some(e=>e.includes($t('zakat_inventory_value')))"
                        class="badge bg-primary bg-opacity-10 text-primary small">
                    <AppIcon name="bi-stars" class="bi bi-stars me-1" style="font-size:0.65rem;" />{{ $t('zakat_from_platform') }}
                  </span>
                  <span v-else-if="fetchedFields.inventory === false" class="badge bg-warning bg-opacity-10 text-warning small">
                    <AppIcon name="bi-pencil" class="bi bi-pencil me-1" style="font-size:0.65rem;" />{{ $t('zakat_manual_entry') }}
                  </span>
                </div>
              </div>
              <div class="input-group" :class="{ 'platform-filled': fetchedFields.inventory && !loadingStates.inventory }">
                <input type="number" class="form-control" v-model.number="assets.inventory" min="0" step="0.01" placeholder="0.00">
                <span class="input-group-text">{{ currency }}</span>
              </div>
              <small class="text-muted mt-1 d-block">{{ $t('zakat_inventory_hint') }}</small>
            </div>

            <!-- Accounts Receivable -->
            <div class="asset-row">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label mb-0 fw-semibold">
                  <AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text text-warning me-2" />
                  {{ $t('zakat_receivables') }}
                </label>
                <div class="d-flex align-items-center gap-1">
                  <span v-if="loadingStates.receivables" class="spinner-border spinner-border-sm text-primary" style="width:14px;height:14px;"></span>
                  <span v-else-if="fetchedFields.receivables && !fetchErrors.some(e=>e.includes($t('zakat_receivables')))"
                        class="badge bg-primary bg-opacity-10 text-primary small">
                    <AppIcon name="bi-stars" class="bi bi-stars me-1" style="font-size:0.65rem;" />{{ $t('zakat_from_platform') }}
                  </span>
                  <span v-else-if="fetchedFields.receivables === false" class="badge bg-warning bg-opacity-10 text-warning small">
                    <AppIcon name="bi-pencil" class="bi bi-pencil me-1" style="font-size:0.65rem;" />{{ $t('zakat_manual_entry') }}
                  </span>
                </div>
              </div>
              <div class="input-group" :class="{ 'platform-filled': fetchedFields.receivables && !loadingStates.receivables }">
                <input type="number" class="form-control" v-model.number="assets.receivables" min="0" step="0.01" placeholder="0.00">
                <span class="input-group-text">{{ currency }}</span>
              </div>
              <small class="text-muted mt-1 d-block">{{ $t('zakat_receivables_hint') }}</small>
            </div>

            <!-- Other Assets -->
            <div class="asset-row" style="border-bottom: none;">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label mb-0 fw-semibold">
                  <AppIcon name="bi-collection" class="bi bi-collection text-info me-2" />
                  {{ $t('zakat_other_assets') }}
                </label>
                <span class="badge bg-secondary bg-opacity-10 text-muted small">{{ $t('zakat_manual_entry') }}</span>
              </div>
              <div class="input-group">
                <input type="number" class="form-control" v-model.number="assets.otherAssets" min="0" step="0.01" placeholder="0.00">
                <span class="input-group-text">{{ currency }}</span>
              </div>
              <small class="text-muted mt-1 d-block">{{ $t('zakat_other_assets_hint') }}</small>
            </div>
          </div>
        </div>

        <!-- Liabilities Section -->
        <div class="card border-0 shadow-sm mb-4 zakat-card">
          <div class="card-body">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div class="d-flex align-items-center gap-2">
                <div class="icon-circle bg-danger-subtle">
                  <AppIcon name="bi-dash-circle" class="bi bi-dash-circle text-danger" />
                </div>
                <div>
                  <h6 class="mb-0 fw-bold">{{ $t('zakat_liabilities') }}</h6>
                  <small class="text-muted">{{ $t('zakat_liabilities_desc') }}</small>
                </div>
              </div>
              <span class="badge bg-danger-subtle text-danger fs-6 px-3 py-2">
                {{ formatMoney(totalLiabilities) }}
              </span>
            </div>

            <!-- Debts & Payables -->
            <div class="asset-row">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label mb-0 fw-semibold">
                  <AppIcon name="bi-credit-card" class="bi bi-credit-card text-danger me-2" />
                  {{ $t('zakat_debts') }}
                </label>
                <div class="d-flex align-items-center gap-1">
                  <span v-if="loadingStates.debts" class="spinner-border spinner-border-sm text-primary" style="width:14px;height:14px;"></span>
                  <span v-else-if="fetchedFields.debts && !fetchErrors.some(e=>e.includes($t('zakat_debts')))"
                        class="badge bg-primary bg-opacity-10 text-primary small">
                    <AppIcon name="bi-stars" class="bi bi-stars me-1" style="font-size:0.65rem;" />{{ $t('zakat_from_platform') }}
                  </span>
                  <span v-else-if="fetchedFields.debts === false" class="badge bg-warning bg-opacity-10 text-warning small">
                    <AppIcon name="bi-pencil" class="bi bi-pencil me-1" style="font-size:0.65rem;" />{{ $t('zakat_manual_entry') }}
                  </span>
                </div>
              </div>
              <div class="input-group" :class="{ 'platform-filled': fetchedFields.debts && !loadingStates.debts }">
                <input type="number" class="form-control" v-model.number="liabilities.debts" min="0" step="0.01" placeholder="0.00">
                <span class="input-group-text">{{ currency }}</span>
              </div>
              <small class="text-muted mt-1 d-block">{{ $t('zakat_debts_hint') }}</small>
            </div>

            <!-- Expenses & Taxes -->
            <div class="asset-row" style="border-bottom: none;">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="form-label mb-0 fw-semibold">
                  <AppIcon name="bi-receipt" class="bi bi-receipt text-secondary me-2" />
                  {{ $t('zakat_expenses') }}
                </label>
                <span class="badge bg-secondary bg-opacity-10 text-muted small">{{ $t('zakat_manual_entry') }}</span>
              </div>
              <div class="input-group">
                <input type="number" class="form-control" v-model.number="liabilities.expenses" min="0" step="0.01" placeholder="0.00">
                <span class="input-group-text">{{ currency }}</span>
              </div>
              <small class="text-muted mt-1 d-block">{{ $t('zakat_expenses_hint') }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Summary -->
      <div class="col-lg-5">
        <!-- Nisab Reference -->
        <div class="card border-0 shadow-sm mb-4 zakat-card">
          <div class="card-body">
            <div class="d-flex align-items-center gap-2 mb-3">
              <div class="icon-circle bg-warning-subtle">
                <AppIcon name="bi-gem" class="bi bi-gem text-warning" />
              </div>
              <div>
                <h6 class="mb-0 fw-bold">{{ $t('zakat_nisab') }}</h6>
                <small class="text-muted">{{ $t('zakat_nisab_desc') }}</small>
              </div>
            </div>

            <!-- Nisab price prompt -->
            <div v-if="goldPricePerGram <= 0 && silverPricePerGram <= 0" class="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2" role="alert">
              <AppIcon name="bi-info-circle-fill" class="bi bi-info-circle-fill flex-shrink-0" />
              <small>{{ $t('zakat_nisab_price_prompt') }}</small>
            </div>

            <div class="row g-3">
              <div class="col-6">
                <label class="form-label small fw-semibold text-muted d-flex align-items-center gap-2">
                  {{ $t('zakat_gold_price') }}
                  <span v-if="isFetchingMetals" class="spinner-border spinner-border-sm" style="width:10px;height:10px;border-width:1px;"></span>
                </label>
                <div class="input-group input-group-sm">
                  <input type="number" class="form-control" v-model.number="goldPricePerGram" min="0" step="0.01" placeholder="0.00" :disabled="isFetchingMetals">
                  <span class="input-group-text">{{ currency }}/g</span>
                </div>
              </div>
              <div class="col-6">
                <label class="form-label small fw-semibold text-muted d-flex align-items-center gap-2">
                  {{ $t('zakat_silver_price') }}
                  <span v-if="isFetchingMetals" class="spinner-border spinner-border-sm" style="width:10px;height:10px;border-width:1px;"></span>
                </label>
                <div class="input-group input-group-sm">
                  <input type="number" class="form-control" v-model.number="silverPricePerGram" min="0" step="0.01" placeholder="0.00" :disabled="isFetchingMetals">
                  <span class="input-group-text">{{ currency }}/g</span>
                </div>
              </div>
            </div>
            <div class="row g-2 mt-2">
              <div class="col-6">
                <div class="nisab-box bg-warning-subtle rounded-3 p-3 text-center">
                  <small class="text-muted d-block mb-1">{{ $t('zakat_gold_nisab') }}</small>
                  <strong class="text-warning">{{ formatMoney(goldNisab) }}</strong>
                  <small class="text-muted d-block">(85g)</small>
                </div>
              </div>
              <div class="col-6">
                <div class="nisab-box bg-secondary bg-opacity-10 rounded-3 p-3 text-center">
                  <small class="text-muted d-block mb-1">{{ $t('zakat_silver_nisab') }}</small>
                  <strong class="text-secondary">{{ formatMoney(silverNisab) }}</strong>
                  <small class="text-muted d-block">(595g)</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Zakat Summary Card -->
        <div class="card border-0 shadow-sm zakat-card zakat-summary-card">
          <div class="card-body">
            <div class="d-flex align-items-center gap-2 mb-4">
              <div class="icon-circle bg-primary-subtle">
                <AppIcon name="bi-calculator" class="bi bi-calculator text-primary" />
              </div>
              <div>
                <h6 class="mb-0 fw-bold">{{ $t('zakat_summary') }}</h6>
                <small v-if="zakatYear" class="text-muted">{{ $t('zakat_year_label') }}: {{ zakatYear }}</small>
              </div>
            </div>

            <!-- Summary Items -->
            <div class="summary-item">
              <span class="text-muted">{{ $t('zakat_total_assets') }}</span>
              <strong class="text-success">{{ formatMoney(totalAssets) }}</strong>
            </div>
            <div class="summary-item">
              <span class="text-muted">{{ $t('zakat_total_liabilities') }}</span>
              <strong class="text-danger">- {{ formatMoney(totalLiabilities) }}</strong>
            </div>
            <hr class="my-3">
            <div class="summary-item">
              <span class="fw-semibold">{{ $t('zakat_net_amount') }}</span>
              <strong :class="netZakatableAmount >= 0 ? 'text-dark' : 'text-danger'" class="fs-5">
                {{ formatMoney(netZakatableAmount) }}
              </strong>
            </div>

            <!-- Liabilities exceed assets note -->
            <div v-if="liabilitiesExceedAssets" class="mt-2 p-2 bg-secondary bg-opacity-10 rounded-2">
              <small class="text-muted">
                <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />
                {{ $t('zakat_liabilities_exceed_assets') }}
              </small>
            </div>

            <!-- Nisab Status -->
            <div class="mt-3 p-3 rounded-3" :class="nisabStatusClass">
              <div class="d-flex align-items-center gap-2">
                <AppIcon name="bi-check-circle-fill" :class="meetsNisab ? 'bi bi-check-circle-fill text-success' : 'bi bi-exclamation-triangle-fill text-warning'" class="fs-5" />
                <div>
                  <strong :class="meetsNisab ? 'text-success' : 'text-warning'" class="d-block">
                    {{ meetsNisab ? $t('zakat_nisab_reached') : $t('zakat_nisab_not_reached') }}
                  </strong>
                  <small class="text-muted" v-if="activeNisab > 0">
                    {{ $t('zakat_nisab_threshold') }}: {{ formatMoney(activeNisab) }}
                  </small>
                  <small class="text-muted" v-else>
                    {{ $t('zakat_nisab_price_prompt') }}
                  </small>
                </div>
              </div>
            </div>

            <!-- Zakat Due -->
            <div class="mt-4 zakat-result p-4 rounded-3 text-center" :class="meetsNisab && zakatDue > 0 ? 'bg-primary bg-opacity-10 border-primary-subtle' : 'bg-light'">
              <small class="text-muted text-uppercase fw-bold d-block mb-2 letter-spacing">{{ $t('zakat_due') }} (2.5%)</small>
              <h2 class="mb-0 fw-bold" :class="meetsNisab && zakatDue > 0 ? 'text-primary' : 'text-muted'">
                {{ formatMoney(zakatDue) }}
              </h2>
              <small v-if="!meetsNisab && activeNisab > 0" class="text-muted mt-2 d-block">{{ $t('zakat_below_nisab_note') }}</small>
              <small v-if="activeNisab <= 0" class="text-muted mt-2 d-block">{{ $t('zakat_set_nisab_to_calculate') }}</small>
            </div>

            <!-- Rate Info -->
            <div class="text-center mt-3">
              <small class="text-muted">
                <AppIcon name="bi-info-circle" class="bi bi-info-circle me-1" />
                {{ $t('zakat_rate_info') }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '@/helper'
import { canPlanAccessModuleItem, getBusinessPlanKey } from '@/constants/pricingPlans'

export default {
  name: 'Zakat',
  data() {
    return {
      zakatYear: '',
      zakatDate: new Date().toISOString().split('T')[0],
      assets: {
        cashBank: 0,
        inventory: 0,
        receivables: 0,
        otherAssets: 0
      },
      liabilities: {
        debts: 0,
        expenses: 0
      },
      goldPricePerGram: 0,
      silverPricePerGram: 0,
      // Loading states per field
      loadingStates: {
        inventory: false,
        receivables: false,
        debts: false
      },
      // Track which fields were successfully fetched from the platform
      fetchedFields: {
        inventory: null,   // null = not yet tried, true = success, false = failed
        receivables: null,
        debts: null
      },
      isFetching: false,
      isFetchingMetals: false,
      fetchErrors: []
    }
  },
  computed: {
    currency() {
      return localStorage.getItem('currentCurrency') || 'dh'
    },
    currentHijriYear() {
      const now = new Date()
      const gregorianYear = now.getFullYear()
      return Math.round((gregorianYear - 622) * (33 / 32)).toString()
    },
    totalAssets() {
      return (this.assets.cashBank || 0) +
        (this.assets.inventory || 0) +
        (this.assets.receivables || 0) +
        (this.assets.otherAssets || 0)
    },
    totalLiabilities() {
      return (this.liabilities.debts || 0) +
        (this.liabilities.expenses || 0)
    },
    liabilitiesExceedAssets() {
      return this.totalLiabilities > this.totalAssets
    },
    netZakatableAmount() {
      return Math.max(0, this.totalAssets - this.totalLiabilities)
    },
    goldNisab() {
      return (this.goldPricePerGram || 0) * 85
    },
    silverNisab() {
      return (this.silverPricePerGram || 0) * 595
    },
    activeNisab() {
      if (this.silverNisab > 0 && this.goldNisab > 0) {
        return Math.min(this.silverNisab, this.goldNisab)
      }
      return this.goldNisab || this.silverNisab || 0
    },
    meetsNisab() {
      if (this.activeNisab <= 0) return false // If no nisab set, don't assume it's met
      return this.netZakatableAmount >= this.activeNisab
    },
    zakatDue() {
      if (!this.meetsNisab) return 0
      return this.netZakatableAmount * 0.025
    },
    nisabStatusClass() {
      if (this.activeNisab <= 0) return 'bg-secondary bg-opacity-10'
      return this.meetsNisab ? 'bg-success bg-opacity-10' : 'bg-warning bg-opacity-10'
    }
  },
  mounted() {
    if (!helper.checkModuleIsEnabled('zakat')) return this.$router.push({ path: '/' })
    const planKey = getBusinessPlanKey(this.$store.getters.BUSINESS)
    if (!canPlanAccessModuleItem(planKey, 'finance', 'view_payments', 'zakat')) return this.$router.push({ path: '/subscribe' })
    this.refetchPlatformData()
  },
  methods: {
    formatMoney(value) {
      return helper.formatMoney(value || 0)
    },
    async refetchPlatformData() {
      this.isFetching = true
      this.fetchErrors = []
      await Promise.all([
        this.fetchInventoryValue(),
        this.fetchReceivables(),
        this.fetchPayables(),
        this.fetchMetalPrices()
      ])
      this.isFetching = false
    },
    async fetchMetalPrices() {
      this.isFetchingMetals = true
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/insights/get-metal-prices?currency=${encodeURIComponent(this.currency)}`)
        if (response.data) {
          this.goldPricePerGram = response.data.gold
          this.silverPricePerGram = response.data.silver
        }
      } catch (error) {
        this.fetchErrors.push(this.$t('zakat_gold_price') + ': ' + this.$t('zakat_fetch_failed'))
      } finally {
        this.isFetchingMetals = false
      }
    },
    resetForm() {
      this.assets = { cashBank: 0, inventory: 0, receivables: 0, otherAssets: 0 }
      this.liabilities = { debts: 0, expenses: 0 }
      this.fetchedFields = { inventory: null, receivables: null, debts: null }
      this.fetchErrors = []
      this.refetchPlatformData()
    },
    async fetchInventoryValue() {
      this.loadingStates.inventory = true
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/insights/total-stock-valuation-cost`)
        if (response.data && response.data.count !== undefined) {
          this.assets.inventory = parseFloat(parseFloat(response.data.count).toFixed(2))
          this.fetchedFields.inventory = true
        } else {
          this.fetchedFields.inventory = false
        }
      } catch (e) {
        this.fetchedFields.inventory = false
        this.fetchErrors.push(this.$t('zakat_inventory_value') + ': ' + this.$t('zakat_fetch_failed'))
      } finally {
        this.loadingStates.inventory = false
      }
    },
    async fetchReceivables() {
      this.loadingStates.receivables = true
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/insights/pending-invoiced-amount`)
        if (response.data && response.data.count !== undefined) {
          this.assets.receivables = parseFloat(parseFloat(response.data.count).toFixed(2))
          this.fetchedFields.receivables = true
        } else {
          this.fetchedFields.receivables = false
        }
      } catch (e) {
        this.fetchedFields.receivables = false
        this.fetchErrors.push(this.$t('zakat_receivables') + ': ' + this.$t('zakat_fetch_failed'))
      } finally {
        this.loadingStates.receivables = false
      }
    },
    async fetchPayables() {
      this.loadingStates.debts = true
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/insights/pending-supplier-invoiced-amount`)
        if (response.data && response.data.count !== undefined) {
          this.liabilities.debts = parseFloat(parseFloat(response.data.count).toFixed(2))
          this.fetchedFields.debts = true
        } else {
          this.fetchedFields.debts = false
        }
      } catch (e) {
        this.fetchedFields.debts = false
        this.fetchErrors.push(this.$t('zakat_debts') + ': ' + this.$t('zakat_fetch_failed'))
      } finally {
        this.loadingStates.debts = false
      }
    }
  }
}
</script>

<style scoped>
.zakat-page {
  max-width: 1200px;
  margin: 0 auto;
}

.zakat-card {
  border-radius: 16px !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.zakat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08) !important;
}

.icon-circle {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.asset-row {
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.asset-row:first-of-type {
  padding-top: 8px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.letter-spacing {
  letter-spacing: 1.5px;
  font-size: 0.7rem !important;
}

.zakat-result {
  border: 2px dashed #dee2e6;
}

.zakat-result.bg-primary {
  border-color: rgba(13, 110, 253, 0.3);
}

.zakat-result.border-primary-subtle {
  border-color: rgba(13, 110, 253, 0.3) !important;
}

.nisab-box {
  transition: transform 0.2s ease;
}

.nisab-box:hover {
  transform: scale(1.02);
}

.zakat-summary-card {
  position: sticky;
  top: 20px;
}

.form-control:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.15rem rgba(13, 110, 253, 0.15);
}

.input-group-text {
  background-color: #f8f9fa;
  border-color: #dee2e6;
  font-size: 0.85rem;
  color: #6c757d;
}

/* Platform-filled field highlight */
.platform-filled .form-control {
  border-color: rgba(13, 110, 253, 0.3);
  background-color: rgba(13, 110, 253, 0.02);
}

.platform-filled .input-group-text {
  border-color: rgba(13, 110, 253, 0.3);
  background-color: rgba(13, 110, 253, 0.05);
  color: rgba(13, 110, 253, 0.7);
}
</style>
